import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../../api/api';
import { Button } from '../ui/button';
import { Landmark, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Estado y acciones de la conexión bancaria (Enable Banking / BBVA).
 * Solo sincroniza movimientos; la vinculación con facturas sigue siendo manual.
 */
const ConexionBancaria = ({ onSyncCompleta }) => {
  const token = getAuthToken();
  const [estado, setEstado] = useState(null);
  const [trabajando, setTrabajando] = useState(false);

  const cargarEstado = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bancos/estado`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setEstado(await res.json());
    } catch (e) {
      console.error('Error cargando estado de conexión bancaria:', e);
    }
  }, [token]);

  useEffect(() => {
    cargarEstado();
    // Aviso al volver del banco (callback añade ?banco=conectado|error)
    const params = new URLSearchParams(window.location.search);
    const resultado = params.get('banco');
    if (resultado) {
      window.history.replaceState({}, '', window.location.pathname);
      if (resultado === 'conectado') {
        alert('Banco conectado correctamente. La primera sincronización se está ejecutando.');
      } else {
        alert('No se pudo completar la conexión con el banco. Inténtalo de nuevo.');
      }
    }
  }, [cargarEstado]);

  const conectar = async () => {
    setTrabajando(true);
    try {
      const res = await fetch(`${API_URL}/api/bancos/conectar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      alert(data.detail || 'No se pudo iniciar la conexión con el banco');
    } catch (e) {
      alert('Error iniciando la conexión con el banco');
    } finally {
      setTrabajando(false);
    }
  };

  const sincronizar = async () => {
    setTrabajando(true);
    try {
      const res = await fetch(`${API_URL}/api/bancos/sincronizar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        alert(
          `Sincronización completada\n\nMovimientos nuevos: ${data.nuevos}\nDuplicados omitidos: ${data.duplicados_omitidos}` +
          (data.facturas_auto_vinculadas ? `\nFacturas de gasto vinculadas: ${data.facturas_auto_vinculadas}` : '')
        );
        cargarEstado();
        if (onSyncCompleta) onSyncCompleta();
      } else {
        alert(data.detail || 'Error en la sincronización');
        cargarEstado();
      }
    } catch (e) {
      alert('Error en la sincronización con el banco');
    } finally {
      setTrabajando(false);
    }
  };

  if (!estado) return null;

  const caducaPronto = estado.conectado && estado.dias_restantes !== null && estado.dias_restantes <= 15;

  return (
    <div className="flex flex-col items-end gap-1">
      {estado.conectado ? (
        <>
          <Button variant="outline" onClick={sincronizar} disabled={trabajando}>
            <RefreshCw className={`w-4 h-4 mr-2 ${trabajando ? 'animate-spin' : ''}`} />
            Sincronizar {estado.banco}
          </Button>
          <p className="text-[11px] text-gray-400">
            {estado.ultima_sync
              ? `Ultima sync: ${new Date(estado.ultima_sync).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}`
              : 'Sin sincronizaciones todavia'}
            {estado.ultimo_resultado ? ` (${estado.ultimo_resultado.nuevos} nuevos)` : ''}
          </p>
          <button
            onClick={conectar}
            className={`text-[11px] underline ${caducaPronto ? 'text-orange-600' : 'text-gray-400'}`}
            disabled={trabajando}
          >
            {caducaPronto
              ? `El consentimiento caduca en ${estado.dias_restantes} dias. Renovar ahora`
              : 'Reconectar banco'}
          </button>
        </>
      ) : (
        <>
          <Button variant="outline" onClick={conectar} disabled={trabajando}>
            <Landmark className="w-4 h-4 mr-2" />
            {estado.estado === 'caducada' ? 'Reconectar banco' : 'Conectar banco'}
          </Button>
          {estado.estado === 'caducada' && (
            <p className="text-[11px] text-orange-600">Consentimiento caducado</p>
          )}
        </>
      )}
    </div>
  );
};

export default ConexionBancaria;
